import { Toolbar, Stack, SxProps, Theme, CircularProgress } from '@mui/material';
import { BottomAppBar, CartBartTypography } from '../appBarStyles';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useAppSelector } from '../../../redux/hooks';
import { CartBarBadgeIcon } from '../../shoppingCart/cartBarBadgeIcon';
import { useRouteNavigator } from '../../../routes/useRouteNavigator';
import { RootState } from '../../../redux/store';
import { roundDoubleNumbers } from '../../../helpers/currency';
import { useEffect, useState } from 'react';

interface CartBarProps {
  barText: string;
  open?: () => void;
  isDisabled?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
  position?: 'fixed' | 'absolute' | 'sticky' | 'static' | 'relative';
  isSmallFont?: boolean;
  isPurchase?: boolean;
  price?: number;
  hideBadge?: boolean;
}

export function CartBar(props: CartBarProps) {
  const routeNavigator = useRouteNavigator();
  const priceToPay = useAppSelector((state: RootState) => state.shoppingCartState.priceToPay);
  const totalPrice = useAppSelector((state: RootState) => state.shoppingCartState.totalPrice);
  const updatingCart = useAppSelector((state: RootState) => state.shoppingCartState.updatingCart);
  const { barText, sx = [], isDisabled = false, isPurchase = false, hideBadge = false } = props;
  const open = props.open ?? routeNavigator.goToCart;
  const [disabled, setDisabled] = useState(isDisabled);
  const [price, setPrice] = useState<number>(props.price ?? 0);

  useEffect(() => {
    setDisabled(isDisabled || updatingCart);
  }, [isDisabled, updatingCart]);

  useEffect(() => {
    if (props.price !== undefined) {
      setPrice(props.price);
      return;
    }
    const value = isPurchase ? priceToPay : totalPrice;
    setPrice(value);
  }, [isPurchase, totalPrice, priceToPay, props.price]);

  const onClick = () => {
    if (disabled) {
      return;
    }
    open();
  };

  return (
    <BottomAppBar
      sx={[
        {
          cursor: !disabled && 'pointer',
          backgroundColor: disabled && '#f0f0f0',
          ':hover': {
            backgroundColor: !disabled && 'secondary.dark',
            boxShadow: !disabled && '1px 1px 5px grey',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      className={props.className}
      position={props.position}
      color="primary"
      onClick={() => onClick()}
    >
      <Toolbar>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
          <Stack direction="row" alignItems="center">
            {updatingCart ? (
              <CircularProgress sx={{ ml: '10px' }} size={28} thickness={4} />
            ) : (
              <>
                {!hideBadge && <CartBarBadgeIcon isSmallFont={props.isSmallFont} />}
                <CartBartTypography isSmallFont={props.isSmallFont}> {roundDoubleNumbers(price)} €</CartBartTypography>
              </>
            )}
          </Stack>
          <Stack direction="row" alignItems="center">
            <CartBartTypography isSmallFont={props.isSmallFont}>{barText}</CartBartTypography>
            <KeyboardArrowRightIcon fontSize={props.isSmallFont ? 'small' : 'medium'} />
          </Stack>
        </Stack>
      </Toolbar>
    </BottomAppBar>
  );
}
